// actions/adminNotificationActions.js
import axios from "../helpers/axios";
import { getSocket, refreshAdminSocketAuth } from "../socket/socket";
import { playDing, speak } from "../utils/notifyAudio";
import {
    notificationActions as C,
    getNotificationId,
    isNotificationObjectId,
} from "../utils/adminNotificationUtils";

let pageSequence = 0;
let countsSequence = 0;
let subscription = null;

const getOwner = (getState) => {
    const auth = getState().auth;
    return auth?.authenticate && ["admin", "superAdmin"].includes(auth.user?.role)
        ? getNotificationId(auth.user) : "";
};
const isCurrent = (getState, ownerId) => !!ownerId
    && getOwner(getState) === ownerId
    && getState().notification.ownerId === ownerId;
const errorMessage = (error, fallback) => error.response?.data?.message || error.response?.data?.error || fallback;

export const loadAdminNotificationCounts = () => async (dispatch, getState) => {
    const { ownerId, activityVersion } = getState().notification;
    if (!isCurrent(getState, ownerId)) return false;
    const requestId = ++countsSequence;
    dispatch({ type: C.COUNTS_REQUEST, payload: { ownerId, requestId } });
    try {
        const res = await axios.get("/admin/notifications/unread-count");
        if (isCurrent(getState, ownerId)) {
            dispatch({ type: C.COUNTS_SUCCESS, payload: { ...res.data, ownerId, requestId, activityVersion } });
        }
        return true;
    } catch (error) {
        if (isCurrent(getState, ownerId)) {
            dispatch({ type: C.COUNTS_FAILURE, payload: { ownerId, requestId, message: errorMessage(error, "Не удалось обновить счётчики уведомлений") } });
        }
        return false;
    }
};

const refreshCounts = (dispatch, getState, ownerId) => {
    if (subscription?.ownerId === ownerId) subscription.scheduleCounts();
    else if (isCurrent(getState, ownerId)) dispatch(loadAdminNotificationCounts());
};

const applyAllRead = (data, dispatch, getState, ownerId) => {
    const state = getState().notification;
    const cutoff = new Date(data.readBefore).getTime();
    if (!Number.isFinite(cutoff)) return;
    if (state.readBefore && cutoff <= new Date(state.readBefore).getTime()) return;
    const reload = state.hasLoaded || state.loading;
    dispatch({ type: C.MARK_ALL_READ, payload: { ownerId, readBefore: data.readBefore } });
    // Список перечитываем: новое событие может иметь ту же миллисекунду, что и readBefore.
    if (reload) dispatch(resetAndLoadFirstPage());
};

export const resetAndLoadFirstPage = (filtersPatch = {}) => async (dispatch, getState) => {
    const ownerId = getState().notification.ownerId;
    if (!isCurrent(getState, ownerId)) return false;
    dispatch({ type: C.SET_FILTERS, payload: filtersPatch });
    dispatch({ type: C.RESET_LIST });
    refreshCounts(dispatch, getState, ownerId);
    return dispatch(loadNextPage());
};

// Старый вызов оставлен для существующих потребителей.
export const fetchAdminNotifications = () => resetAndLoadFirstPage();

export const loadNextPage = () => async (dispatch, getState) => {
    const { ownerId, nextCursor, nextCursorId, hasMore, loading, filters, generation } = getState().notification;
    if (!isCurrent(getState, ownerId) || !hasMore || loading) return false;
    const requestId = ++pageSequence;
    dispatch({ type: C.FETCH_PAGE_REQUEST, payload: { ownerId, requestId } });
    try {
        const res = await axios.get("/admin/notifications", {
            params: {
                limit: filters.limit,
                sort: filters.sort,
                from: filters.from || undefined,
                to: filters.to || undefined,
                type: filters.type || undefined,
                unread: filters.unread ? 1 : undefined,
                cursor: nextCursor || undefined,
                cursorId: nextCursorId || undefined,
            },
        });
        if (!isCurrent(getState, ownerId)) return false;
        dispatch({
            type: C.FETCH_PAGE_SUCCESS,
            payload: {
                ownerId, requestId, generation,
                items: res.data.notifications || [],
                nextCursor: res.data.paging?.nextCursor || null,
                nextCursorId: res.data.paging?.nextCursorId || null,
                hasMore: !!res.data.paging?.hasMore,
            },
        });
        return true;
    } catch (error) {
        if (isCurrent(getState, ownerId)) {
            dispatch({ type: C.FETCH_PAGE_FAILURE, payload: { ownerId, requestId, generation, message: errorMessage(error, "Не удалось загрузить уведомления") } });
        }
        return false;
    }
};

export const markAdminNotificationRead = (id) => async (dispatch, getState) => {
    const ownerId = getState().notification.ownerId;
    if (!isCurrent(getState, ownerId) || !isNotificationObjectId(id)) return false;
    try {
        const res = await axios.put("/admin/notifications/read/" + id);
        if (!isCurrent(getState, ownerId)) return false;
        dispatch({ type: C.MARK_READ, payload: { ownerId, id, notification: res.data.notification } });
        refreshCounts(dispatch, getState, ownerId);
        return true;
    } catch (error) {
        console.error("Ошибка отметки уведомления:", errorMessage(error, "Не удалось отметить уведомление"));
        return false;
    }
};

export const markAllAdminNotificationsRead = () => async (dispatch, getState) => {
    const ownerId = getState().notification.ownerId;
    if (!isCurrent(getState, ownerId)) return false;
    try {
        const res = await axios.put("/admin/notifications/read-all");
        if (!isCurrent(getState, ownerId)) return false;
        applyAllRead(res.data, dispatch, getState, ownerId);
        refreshCounts(dispatch, getState, ownerId);
        return true;
    } catch (error) {
        console.error("Ошибка чтения всех уведомлений:", errorMessage(error, "Не удалось прочитать все уведомления"));
        return false;
    }
};

const showNewNotification = (data) => {
    if (typeof window === "undefined") return;
    if (!document.hidden) {
        try {
            Promise.resolve(playDing()).catch(() => {});
            speak(data.title || "Новое уведомление");
        } catch (_error) {}
        window.dispatchEvent(new CustomEvent("admin-toast", { detail: data }));
    } else if (window.Notification?.permission === "granted") {
        try {
            const notice = new window.Notification(data.title || "Новое уведомление", {
                body: data.message || "Откройте админку",
                icon: "/favicon.ico",
                tag: "admin-notification-" + getNotificationId(data),
            });
            notice.onclick = () => {
                window.focus();
                window.dispatchEvent(new CustomEvent("admin-notification:open", { detail: data }));
                notice.close();
            };
        } catch (_error) {}
    }
};

// Подписка живёт в App, поэтому смена страницы не создаёт новых обработчиков.
export const subscribeAdminNotifications = () => (dispatch, getState) => {
    const ownerId = getOwner(getState);
    if (!ownerId) return () => {};
    if (subscription) subscription.stop();
    dispatch({ type: C.SET_OWNER, payload: { ownerId } });
    const socket = getSocket();
    const seen = new Set();
    let timer = null;
    let stopped = false;
    const active = () => !stopped && isCurrent(getState, ownerId);
    const scheduleCounts = () => {
        if (!active()) return;
        clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            if (active()) dispatch(loadAdminNotificationCounts());
        }, 60);
    };
    const onNew = (data) => {
        if (!active() || !isNotificationObjectId(getNotificationId(data))) return;
        const id = getNotificationId(data);
        const duplicate = seen.has(id) || getState().notification.notifications.some((item) => getNotificationId(item) === id);
        seen.add(id);
        if (seen.size > 500) seen.delete(seen.values().next().value);
        dispatch({ type: C.RECEIVED, payload: { ownerId, notification: data } });
        scheduleCounts();
        if (!duplicate) showNewNotification(data);
    };
    const onRead = (data) => {
        if (!active()) return;
        if (data?.all && data.readBefore) {
            applyAllRead(data, dispatch, getState, ownerId);
        } else if (isNotificationObjectId(data?.id)) {
            dispatch({ type: C.MARK_READ, payload: { ownerId, id: data.id } });
        } else return;
        scheduleCounts();
    };
    const onConnect = () => {
        socket.emit("admin:join", (ack) => {
            if (!active()) return;
            scheduleCounts();
            if (ack?.ok && getState().notification.hasLoaded) dispatch(resetAndLoadFirstPage());
        });
    };
    const onFocus = () => scheduleCounts();
    const stop = () => {
        if (stopped) return;
        stopped = true;
        clearTimeout(timer);
        socket.off("connect", onConnect);
        socket.off("admin:notification:new", onNew);
        socket.off("admin:notification:read", onRead);
        window.removeEventListener("focus", onFocus);
        if (subscription?.stop === stop) subscription = null;
    };
    subscription = { ownerId, stop, scheduleCounts, receive: onNew };
    socket.on("connect", onConnect);
    socket.on("admin:notification:new", onNew);
    socket.on("admin:notification:read", onRead);
    window.addEventListener("focus", onFocus);
    refreshAdminSocketAuth();
    if (socket.connected) onConnect();
    dispatch(loadAdminNotificationCounts());
    return () => {
        stop();
        if (!getOwner(getState) || getOwner(getState) !== ownerId) {
            dispatch({ type: C.CLEAR, payload: { ownerId } });
        }
    };
};

export const createAdminOrderNotification = () => async (dispatch, getState) => {
    const ownerId = getState().notification.ownerId;
    if (!isCurrent(getState, ownerId)) return false;
    try {
        const res = await axios.post("/admin/notifications", {
            type: "order", title: "Новый заказ", message: "Новый заказ создан", link: "/orders",
        });
        if (!isCurrent(getState, ownerId)) return false;
        if (subscription?.ownerId === ownerId) subscription.receive(res.data.notification);
        else {
            dispatch({ type: C.RECEIVED, payload: { ownerId, notification: res.data.notification } });
            dispatch(loadAdminNotificationCounts());
        }
        return true;
    } catch (error) {
        console.error("Ошибка создания тестового уведомления:", errorMessage(error, "Не удалось создать уведомление"));
        return false;
    }
};
